// Consultation/ConsultationDetail.tsx

import React, {useContext, useEffect, useState} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import {axios} from 'Auth/axiosConfig';
import {useErrorProcessing} from 'Core/components/ErrorProvider';
import CircularProgress from 'Core/components/elements/CircularProgress';
import {AuthContext, AuthContextType} from 'Auth/AuthContext';
import {useTheme} from 'Theme/ThemeContext';
import pprint from '../../Utils/pprint';
import {IConsultation} from '../../Types/psychology/consultation';
import {FC, FCCC, FR, FRSC} from 'WideLayout/Layouts';
import ConsultationActions from 'Consultation/ConsultationActions';
import ConsultationConfigDesc from './ConsultationConfigDesc';
import UserConsultationRoleCard from './UserConsultationRoleCard';
import ConsultationStatus from "Consultation/ConsultationStatus";

interface ConsultationDetailProps {
    className?: string;
}

const ConsultationDetail: React.FC<ConsultationDetailProps> = ({className}) => {
    const {isAuthenticated, user: currentUser} = useContext(AuthContext) as AuthContextType;
    const {id} = useParams<{ id: string }>();
    const [consultation, setConsultation] = useState<IConsultation | null>(null);
    const {plt} = useTheme();
    const navigate = useNavigate();
    const {byResponse, notAuthentication} = useErrorProcessing();
    const [isLoading, setIsLoading] = useState<boolean>(false); // Loading state

    useEffect(() => {
        if (!isAuthenticated) {
            notAuthentication();
            return;
        }
        setIsLoading(true);
        axios.get(`/api/v1/consultations/${id}/`)
            .then(response => {
                pprint(`Consultation ID: ${id}`);
                pprint(response.data);
                setConsultation(response.data);
            })
            .catch(error => byResponse(error))
            .finally(() => setIsLoading(false));
    }, [id, byResponse, isAuthenticated, notAuthentication]);

    if (!consultation) return <CircularProgress size={'100px'}/>;

    const {
        clients,
        config,
        psychologists,
        language,
        date,
        status,
        expired_at,
        created_at,
        updated_at,
        conference,
    } = consultation;

    const formatDate = (dateString: string): string => {
        const date = new Date(dateString);
        const now = new Date();
        const options: Intl.DateTimeFormatOptions = {
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        };

        if (date.getFullYear() === now.getFullYear()) {
            return date.toLocaleString('ru-RU', options);
        } else {
            return date.toLocaleString('ru-RU', {...options, year: 'numeric'});
        }
    };

    const handleConsultationUpdate = (newConsultation: IConsultation) => {
        setConsultation(newConsultation);
    }
    return (
        <FC scroll="y-auto" cls={`no-scrollbar ${className}`}>
            <FC p={2} pt={1}>
                {/* Status */}
                <FRSC mt={1} gap={1}>
                    <span className="fs-5 fw-4 lh-1"
                          style={{
                              lineHeight: '1.4rem'
                          }}>
                        <ConsultationStatus cls={'fw-bold'} consultation={consultation}/>
                    </span>
                    <FR w={'min-content'} mt={'2px'} fontSize={'1rem'}
                        color={plt.text.primary25}>
                        ID:{consultation.id}
                    </FR>
                    {currentUser?.is_staff && <FR w={'min-content'} mt={'2px'} fontSize={'.8rem'}
                                                  py={'.1rem'} px={'.5rem'} rounded={3}
                                                  bg={plt.colors.secondary.lighter} cls={'text-nowrap'}
                                                  color={plt.text.primary45} onClick={() => {
                        window.open(`/lhv2/psychology/consultation/${consultation.id}/change/`, '_blank');
                    }}>
                        In admin
                    </FR>}
                </FRSC>
                {config && <ConsultationConfigDesc
                    config={config}
                    language={language}
                    className="fw-3 mt-1 mb-2"
                    sx={{
                        color: plt.text.primary80,
                        fontSize: '1.1rem',
                    }}
                />}
                {/* Clients */}
                <FC bg={plt.colors.secondary.lighter}
                    w={'fit-content'} p={1} pr={2} g={'.5rem'} rounded={3}>
                    <FC pos="relative" g={1} color={plt.text.primary80}>
                        <FRSC flexWrap="wrap">
                            {clients && clients.map((client) => (
                                <UserConsultationRoleCard
                                    key={client.id}
                                    user={client}
                                    role="клиент"
                                />
                            ))}
                        </FRSC>
                    </FC>

                    {/* Psychologists */}
                    <FC pos="relative" g={1} color={plt.text.primary80}>
                        <FRSC flexWrap="wrap">
                            {psychologists && psychologists.map((psychologist) => (
                                <UserConsultationRoleCard
                                    key={psychologist.id}
                                    user={psychologist}
                                    role="психолог"
                                />
                            ))}
                        </FRSC>
                    </FC>
                </FC>

                {/* Actions */}
                <ConsultationActions
                    clsFeedbackForm={'mt-3'}
                    consultation={consultation}
                    extended={true}
                    onConsultationCancel={handleConsultationUpdate}
                    onConsultationReschedule={handleConsultationUpdate}
                    onConsultationUpdate={handleConsultationUpdate}
                    setLoading={setIsLoading}
                />

                {/* Dates Information */}
                <FC mt={1} cls="fw-3" color={plt.text.primary30}>
                    <span>Создание: {formatDate(created_at)}</span>
                    <span>Изменение: {formatDate(updated_at)}</span>
                    {date && <span>Проведение: {formatDate(date)}</span>}
                    {expired_at && <span>Истекает: {formatDate(expired_at)}</span>}
                </FC>

                {/* Loading Overlay */}
                {isLoading && (
                    <FCCC
                        pos="absolute"
                        cls="backdrop-blur-10"
                        top={0}
                        left={0}
                        zIndex={8}
                        rounded={3}
                        width="100%"
                        height="100%"
                    >
                        <CircularProgress size="3rem"/>
                    </FCCC>
                )}
            </FC>
        </FC>
    );
};

export default ConsultationDetail;
